import React, { useEffect, useState } from "react";
import { DockviewApi } from "dockview-react";
import { api } from "./utils/api";
import { TickerLink } from "./TickerActionMenu";

// Halts: currently halted tickers (backend market_data/halts, fed from Nasdaq Trader's trade-halts list every 15 s),
// same columns and order as nasdaqtrader.com/trader.aspx?id=tradehalts (newest halt first). All times are NY time.

const POLL_MS = 5000;
const NY = "America/New_York";

interface IHalt {
  issue_symbol: string;
  halt_datetime: string;
  issue_name: string;
  market: string;
  reason_code: string;
  resumption_quote_datetime: string | null;
  resumption_trade_datetime: string | null;
}

interface HaltsProps {
  containerApi?: DockviewApi;
}

function nyDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: NY,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function nyTime(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { timeZone: NY, hour12: false });
}

// "EST" in winter, "EDT" in summer (current New York time)
function nyZoneName(): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone: NY, timeZoneName: "short" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName");
  return part ? part.value : "ET";
}

const HEADERS = [
  "Halt Date",
  "Halt Time",
  "Issue Symbol",
  "Issue Name",
  "Market",
  "Reason Codes",
  "Resumption Date",
  "Resumption Quote Time",
  "Resumption Trade Time",
];

export const Halts: React.FC<HaltsProps> = ({ containerApi }) => {
  const [rows, setRows] = useState<IHalt[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get<IHalt[]>("/halts");
        if (!cancelled) {
          // keep the old array when nothing changed, so an unchanged poll doesn't re-render
          setRows((prev) => (JSON.stringify(prev) === JSON.stringify(res.data) ? prev : res.data));
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="halts-root h-full overflow-auto">
      <style>{`
        .halts-root, .halts-root table, .halts-root th, .halts-root td {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 15px;
        }
        .halts-root table { width: 100%; border-collapse: collapse; margin: 0; }
        .halts-root th, .halts-root td {
          padding: 6px 10px;
          text-align: left;
          vertical-align: top;
          border: none;
          border-bottom: 1px solid #e5e7eb;
          background: transparent;
          color: #111827;
          font-weight: 400;
          white-space: nowrap;
        }
        .halts-root th {
          position: sticky; top: 0; z-index: 1;
          background: #f3f4f6;
          color: #374151;
          font-weight: 600;
          white-space: normal;
        }
        .halts-root tbody td:first-child { color: #111827 !important; font-weight: 400 !important; }
        .halts-root td.halts-bold { font-weight: 700; }
        .halts-root tbody tr:hover td { background: #f9fafb; }
        .halts-root td.halts-name { white-space: normal; min-width: 160px; }
      `}</style>
      <div className="px-2 py-1.5 text-gray-500" style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: 14 }}>
        All times are New York time ({nyZoneName()}), daylight saving included.
        {error && <span className="ml-2 text-red-700">Connection error, retrying…</span>}
      </div>
      <table>
        <thead>
          <tr>
            {HEADERS.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // resumption date is shared by quote and trade time; take it from whichever is present
            const resumption = r.resumption_quote_datetime || r.resumption_trade_datetime;
            return (
              <tr key={r.issue_symbol}>
                <td>{nyDate(r.halt_datetime)}</td>
                <td>{nyTime(r.halt_datetime)}</td>
                <td>
                  <TickerLink ticker={r.issue_symbol} containerApi={containerApi} className="font-bold cursor-pointer hover:underline" />
                </td>
                <td className="halts-name">{r.issue_name}</td>
                <td>{r.market}</td>
                <td>{r.reason_code}</td>
                <td className="halts-bold">{nyDate(resumption)}</td>
                <td className="halts-bold">{nyTime(r.resumption_quote_datetime)}</td>
                <td className="halts-bold">{nyTime(r.resumption_trade_datetime)}</td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={HEADERS.length} className="text-center text-gray-500">
                No halted tickers
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
